import React, { useEffect, useState } from "react"
import { useForm, useFieldArray } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FiPlus, FiTrash2, FiCode, FiCheckCircle, FiClock, FiTarget } from "react-icons/fi"
import { toast } from "react-hot-toast"

import { setStep, resetCourseState } from "../../../../../slices/courseSlice"
import { createAssessment, getInstructorAssessment } from "../../../../../services/operations/assessmentAPI"
import { ACCOUNT_TYPE } from "../../../../../utils/constants"

const LANGUAGES = [
  { value: "javascript", label: "JavaScript" },
  { value: "python",     label: "Python"     },
  { value: "java",       label: "Java"       },
  { value: "c_cpp",      label: "C / C++"    },
  { value: "csharp",     label: "C#"         },
  { value: "golang",     label: "Go"         },
  { value: "typescript", label: "TypeScript" },
]

const OPTION_LABELS = ["A", "B", "C", "D"]

const defaultValues = {
  timeLimit:            30,
  passingScore:         70,
  weights:              { mcq: 25, code: 50, project: 25 },
  mcqQuestions:         [],
  codeProblem:          { title: "", description: "", starterCode: "", language: "javascript" },
  projectInstructions:  "",
}

export default function AssessmentForm() {
  const dispatch   = useDispatch()
  const navigate   = useNavigate()
  const { token }  = useSelector((s) => s.auth)
  const { course } = useSelector((s) => s.course)

  const { user }   = useSelector((s) => s.profile)
  const isAdmin    = user?.accountType === ACCOUNT_TYPE.ADMIN

  const [loading,  setLoading]  = useState(false)
  const [fetching, setFetching] = useState(true)

  const { register, control, handleSubmit, reset, watch, formState: { errors } } = useForm({ defaultValues })

  const { fields: mcqFields, append: appendMCQ, remove: removeMCQ } = useFieldArray({
    control,
    name: "mcqQuestions",
  })

  // Load existing assessment if one already exists for this course
  useEffect(() => {
    if (!course?._id) { setFetching(false); return }
    ;(async () => {
      const existing = await getInstructorAssessment(course._id, token, isAdmin)
      if (existing) {
        reset({
          timeLimit:           existing.timeLimit   ?? 30,
          passingScore:        existing.passingScore ?? 70,
          weights:             existing.weights      ?? { mcq: 25, code: 50, project: 25 },
          mcqQuestions:        (existing.mcqQuestions ?? []).map((q) => ({
            question:     q.question     ?? "",
            options:      q.options      ?? ["", "", "", ""],
            correctIndex: q.correctIndex ?? 0,
            topic:        q.topic        ?? "",
          })),
          codeProblem: {
            title:       existing.codeProblem?.title       ?? "",
            description: existing.codeProblem?.description ?? "",
            starterCode: existing.codeProblem?.starterCode ?? "",
            language:    existing.codeProblem?.language    ?? "javascript",
          },
          projectInstructions: existing.projectInstructions ?? "",
        })
      }
      setFetching(false)
    })()
  }, [course?._id])

  const addQuestion = () => {
    appendMCQ({ question: "", options: ["", "", "", ""], correctIndex: 0, topic: "" })
  }

  const weightsTotal = () => {
    const w = watch("weights")
    return Number(w?.mcq ?? 0) + Number(w?.code ?? 0) + Number(w?.project ?? 0)
  }

  const onSave = async (data) => {
    if (!course?._id) return
    const total = Number(data.weights.mcq) + Number(data.weights.code) + Number(data.weights.project)
    if (total !== 100) { toast.error("Weights must sum to 100%"); return }

    setLoading(true)
    const payload = {
      ...data,
      timeLimit:    Number(data.timeLimit),
      passingScore: Number(data.passingScore),
      weights:      { mcq: Number(data.weights.mcq), code: Number(data.weights.code), project: Number(data.weights.project) },
      mcqQuestions: data.mcqQuestions.map((q) => ({
        ...q,
        correctIndex: Number(q.correctIndex),
        options: [q.options[0], q.options[1], q.options[2], q.options[3]],
      })),
    }

    const res = await createAssessment(course._id, payload, token, isAdmin)
    setLoading(false)
    if (res?.success) {
      toast.success("Assessment saved!")
      dispatch(resetCourseState())
      navigate("/dashboard/my-courses")
    } else {
      toast.error(res?.message || "Failed to save assessment")
    }
  }

  const handleSkip = () => {
    dispatch(resetCourseState())
    navigate("/dashboard/my-courses")
  }

  if (fetching) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-yellow-50 border-t-transparent" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-6">

      {/* ── Settings ─────────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-richblack-700 bg-richblack-800 p-5 space-y-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-richblack-5">
          <FiTarget size={16} className="text-yellow-50" /> Assessment Settings
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-richblack-200">
              Time Limit (minutes)
            </label>
            <input
              type="number" min={5} max={180}
              {...register("timeLimit", { required: true, min: 5 })}
              className="w-full rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-richblack-200">
              Passing Score (%)
            </label>
            <input
              type="number" min={1} max={100}
              {...register("passingScore", { required: true, min: 1, max: 100 })}
              className="w-full rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
            />
          </div>
        </div>

        {/* Weights */}
        <div>
          <label className="mb-2 block text-xs font-medium text-richblack-200">
            Section Weights (must total 100%)
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[["mcq", "MCQ Quiz"], ["code", "Code Challenge"], ["project", "Project Review"]].map(([key, label]) => (
              <div key={key}>
                <p className="mb-1 text-[10px] text-richblack-400">{label}</p>
                <div className="flex items-center gap-1">
                  <input
                    type="number" min={0} max={100}
                    {...register(`weights.${key}`, { required: true })}
                    className="w-full rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
                  />
                  <span className="text-xs text-richblack-400">%</span>
                </div>
              </div>
            ))}
          </div>
          {weightsTotal() !== 100 && (
            <p className="mt-1 text-xs text-pink-300">
              Total: {weightsTotal()}% — must equal 100%
            </p>
          )}
        </div>
      </section>

      {/* ── MCQ Questions ────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-richblack-700 bg-richblack-800 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-richblack-5">
            <FiCheckCircle size={16} className="text-yellow-50" />
            MCQ Questions
            <span className="rounded-full bg-richblack-700 px-2 py-0.5 text-xs font-normal text-richblack-400">
              {mcqFields.length}
            </span>
          </h3>
          <button
            type="button"
            onClick={addQuestion}
            className="flex items-center gap-1.5 rounded-xl border border-yellow-50 px-3 py-1.5 text-xs font-semibold text-yellow-50 transition-colors hover:bg-yellow-50/10"
          >
            <FiPlus size={13} /> Add Question
          </button>
        </div>

        {mcqFields.length === 0 && (
          <div className="rounded-xl border border-dashed border-richblack-600 py-8 text-center">
            <p className="text-sm text-richblack-400">No questions yet. Click "Add Question" to start.</p>
          </div>
        )}

        <div className="space-y-5">
          {mcqFields.map((field, qi) => (
            <div key={field.id} className="rounded-xl border border-richblack-600 bg-richblack-700/50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <span className="mt-0.5 text-xs font-bold text-richblack-400">Q{qi + 1}</span>
                <button
                  type="button"
                  onClick={() => removeMCQ(qi)}
                  className="text-pink-400 transition-colors hover:text-pink-300"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>

              {/* Question text */}
              <div>
                <label className="mb-1 block text-xs font-medium text-richblack-200">Question</label>
                <textarea
                  rows={2}
                  placeholder="Enter your question here…"
                  {...register(`mcqQuestions.${qi}.question`, { required: true })}
                  className="w-full resize-none rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
                />
              </div>

              {/* Options */}
              <div>
                <label className="mb-1 block text-xs font-medium text-richblack-200">Options</label>
                <div className="space-y-2">
                  {OPTION_LABELS.map((label, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={oi}
                        {...register(`mcqQuestions.${qi}.correctIndex`)}
                        className="accent-caribbeangreen-300 h-4 w-4 cursor-pointer"
                      />
                      <span className="w-5 text-xs font-bold text-richblack-400">{label}</span>
                      <input
                        type="text"
                        placeholder={`Option ${label}`}
                        {...register(`mcqQuestions.${qi}.options.${oi}`, { required: true })}
                        className="flex-1 rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-1.5 text-sm text-richblack-5 outline-none focus:border-yellow-50"
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-richblack-500">Select the radio button next to the correct answer.</p>
              </div>

              {/* Topic */}
              <div>
                <label className="mb-1 block text-xs font-medium text-richblack-200">Topic (for gap analysis)</label>
                <input
                  type="text"
                  placeholder="e.g. React Hooks, Closures…"
                  {...register(`mcqQuestions.${qi}.topic`)}
                  className="w-full rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
                />
              </div>
            </div>
          ))}
        </div>

        {mcqFields.length > 0 && (
          <button
            type="button"
            onClick={addQuestion}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-richblack-600 py-3 text-sm font-medium text-richblack-400 transition-colors hover:border-richblack-500 hover:text-richblack-200"
          >
            <FiPlus size={14} /> Add Another Question
          </button>
        )}
      </section>

      {/* ── Code Challenge ───────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-richblack-700 bg-richblack-800 p-5 space-y-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-richblack-5">
          <FiCode size={16} className="text-yellow-50" /> Code Challenge
        </h3>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-richblack-200">Problem Title</label>
            <input
              type="text"
              placeholder="e.g. Reverse a linked list"
              {...register("codeProblem.title")}
              className="w-full rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-richblack-200">Language</label>
            <select
              {...register("codeProblem.language")}
              className="w-full rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-richblack-200">Problem Description</label>
          <textarea
            rows={4}
            placeholder="Describe the problem, expected input/output, constraints…"
            {...register("codeProblem.description")}
            className="w-full resize-none rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-richblack-200">Starter Code (optional)</label>
          <textarea
            rows={5}
            placeholder="// starter code for students…"
            {...register("codeProblem.starterCode")}
            className="w-full resize-none rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 font-mono text-sm text-richblack-5 outline-none focus:border-yellow-50"
          />
        </div>
      </section>

      {/* ── Project Instructions ─────────────────────────────────────────── */}
      <section className="rounded-2xl border border-richblack-700 bg-richblack-800 p-5 space-y-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-richblack-5">
          <FiClock size={16} className="text-yellow-50" /> Project Instructions
        </h3>
        <textarea
          rows={5}
          placeholder="Describe what the student should build and submit as a project URL…"
          {...register("projectInstructions")}
          className="w-full resize-none rounded-xl border border-richblack-600 bg-richblack-700 px-3 py-2 text-sm text-richblack-5 outline-none focus:border-yellow-50"
        />
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 border-t border-richblack-700 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          disabled={loading}
          onClick={() => dispatch(setStep(3))}
          className="order-last w-full rounded-xl border border-richblack-600 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 disabled:opacity-50 sm:order-first sm:w-auto sm:px-5"
        >
          ← Back
        </button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            disabled={loading}
            onClick={handleSkip}
            className="w-full rounded-xl border border-richblack-600 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 disabled:opacity-50 sm:w-auto sm:px-5"
          >
            Skip for now
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-yellow-50 py-2.5 text-sm font-semibold text-richblack-900 transition-colors hover:bg-yellow-100 disabled:opacity-50 sm:w-auto sm:px-6"
          >
            {loading ? "Saving…" : "Save Assessment"}
          </button>
        </div>
      </div>
    </form>
  )
}
